import { createField, findNestedField } from '@formie/parse/formieParser';
import { useFormField } from '@formie/useForm';
import { Box, BoxProps, FormControl, FormHelperText, FormLabel, Stack } from '@primitives';
import { gql } from 'graphql-request';
import { DefaultSelectField } from './DefaultSelectField';
import { DefaultTextField } from './DefaultTextField';
import { FieldErrors } from './FieldErrors';
import { Field_Text } from './Field_Text';

gql`
  fragment fieldName on Field_Name {
    ...fieldBase
    useMultipleFields
    fields {
      __typename
      handle
      label
      enabled
      required
      placeholder
      defaultValue
      prePopulate
      errorMessage
      ... on Field_NamePrefix {
        options {
          ...fieldOption
        }
      }
    }
  }
`;

const Field_NameSingle = createField<'Field_Name'>((props) => {
  return <Field_Text {...props} />;
});

const Field_NameMulti = createField<'Field_Name'>(({ field, ..._props }) => {
  const { fields, handle, label, instructions, labelPosition } = field;

  const prefix = findNestedField(fields, 'Field_NamePrefix');
  const firstName = findNestedField(fields, 'Field_NameFirst');
  const middleName = findNestedField(fields, 'Field_NameMiddle');
  const lastName = findNestedField(fields, 'Field_NameLast');

  const { value, setValue, error, errors } = useFormField(handle, 'complex', {
    ...(firstName?.enabled && { firstName: firstName.defaultValue ?? '' }),
    ...(middleName?.enabled && { middleName: middleName.defaultValue ?? '' }),
    ...(lastName?.enabled && { lastName: lastName.defaultValue ?? '' }),
    ...(prefix?.enabled && { prefix: prefix.defaultValue ?? '' }),
  });

  const handleChange =
    (fieldName: string) => (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValue({ ...value, [fieldName]: ev.target.value });
    };

  const fieldWrapProps: BoxProps = {
    minWidth: 100,
    maxWidth: '50%',
  };

  return (
    <FormControl error={error} fullWidth>
      <FormLabel component="legend" sx={{ mb: 2 }} hidden={labelPosition === 'hidden'}>
        {label}
      </FormLabel>
      {instructions && <FormHelperText>{instructions}</FormHelperText>}

      <Stack direction="row" columnGap={2} rowGap={4} flexWrap="wrap">
        {prefix?.enabled && (
          <Box flexGrow={2} {...fieldWrapProps}>
            <DefaultSelectField
              {...{
                name: 'prefix',
                onChange: handleChange('prefix'),
                value: value['prefix'] ?? '',
                helperText: prefix.errorMessage,
                label: prefix.label,
                placeholder: prefix.placeholder,
                required: prefix.required,
                options: prefix.options,
              }}
            />
          </Box>
        )}
        {firstName?.enabled && (
          <Box flexGrow={3} {...fieldWrapProps}>
            <DefaultTextField
              {...{
                name: 'firstName',
                onChange: handleChange('firstName'),
                value: value['firstName'] ?? '',
                helperText: firstName.errorMessage,
                label: firstName.label,
                placeholder: firstName.placeholder,
                required: firstName.required,
              }}
            />
          </Box>
        )}
        {middleName?.enabled && (
          <Box flexGrow={3} {...fieldWrapProps}>
            <DefaultTextField
              {...{
                name: 'middleName',
                onChange: handleChange('middleName'),
                value: value['middleName'] ?? '',
                helperText: middleName.errorMessage,
                label: middleName.label,
                placeholder: middleName.placeholder,
                required: middleName.required,
              }}
            />
          </Box>
        )}
        {lastName?.enabled && (
          <Box flexGrow={3} {...fieldWrapProps}>
            <DefaultTextField
              {...{
                name: 'lastName',
                onChange: handleChange('lastName'),
                value: value['lastName'] ?? '',
                helperText: lastName.errorMessage,
                label: lastName.label,
                placeholder: lastName.placeholder,
                required: lastName.required,
              }}
            />
          </Box>
        )}
      </Stack>

      {errors && <FieldErrors errors={errors} />}
    </FormControl>
  );
});

export const Field_Name = createField<'Field_Name'>((props) => {
  const { field } = props;

  const { useMultipleFields } = field;

  return useMultipleFields ? <Field_NameMulti {...props} /> : <Field_NameSingle {...props} />;
});
